import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  ConfirmDialog,
  ResultDialog,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { depositsApi } from "@/services/api";
import {
  ArrowDownRight,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  HelpCircle,
  Play,
  ExternalLink,
  Wallet,
  Building2,
  Coins,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface Deposit {
  id: string;
  endToEndId: string;
  txId?: string;
  payerDocument: string;
  payerName?: string;
  payerBank?: string;
  amountCents: number;
  pixDate: string;
  pixDescription?: string;
  status: "PENDING" | "PROCESSING" | "MINTED" | "FAILED" | "UNMATCHED";
  companyId?: string;
  mintTxHash?: string;
  mintedAt?: string;
  walletAddress?: string;
  errorMessage?: string;
  retryCount: number;
  createdAt: string;
}

interface Stats {
  total: number;
  pending: number;
  processing: number;
  minted: number;
  failed: number;
  unmatched: number;
  totalMintedBRL: number;
}

interface MinterStatus {
  minterAddress: string | null;
  hasMinterRole: boolean;
  roleError?: string;
  balance: string;
  balanceError?: string;
  isReady: boolean;
}

const STATUS_CONFIG = {
  PENDING: {
    label: "Pendente",
    variant: "secondary" as const,
    icon: Clock,
    description: "Aguardando processamento",
  },
  PROCESSING: {
    label: "Processando",
    variant: "warning" as const,
    icon: Loader2,
    description: "Mint em andamento",
  },
  MINTED: {
    label: "Mintado",
    variant: "success" as const,
    icon: CheckCircle,
    description: "BRLD enviado com sucesso",
  },
  FAILED: {
    label: "Falhou",
    variant: "destructive" as const,
    icon: XCircle,
    description: "Erro no processamento",
  },
  UNMATCHED: {
    label: "Sem Match",
    variant: "warning" as const,
    icon: HelpCircle,
    description: "CNPJ nao encontrado",
  },
};

export function DepositsPage() {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [minterStatus, setMinterStatus] = useState<MinterStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [total, setTotal] = useState(0);

  // Confirm dialog
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    onConfirm: () => void;
    loading: boolean;
  }>({
    open: false,
    title: "",
    description: "",
    onConfirm: () => {},
    loading: false,
  });

  // Result dialog
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    variant: "success" | "error";
    title: string;
    description?: string;
    details?: { label: string; value: string | number }[];
  }>({
    open: false,
    variant: "success",
    title: "",
  });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  async function loadData() {
    setLoading(true);
    try {
      const [depositsData, statsData, minterData] = await Promise.all([
        depositsApi.list({ status: statusFilter || undefined, limit: 100 }),
        depositsApi.getStats(),
        depositsApi.getMinterStatus(),
      ]);
      setDeposits(depositsData.deposits);
      setTotal(depositsData.total);
      setStats(statsData);
      setMinterStatus(minterData);
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
    } finally {
      setLoading(false);
    }
  }

  async function handleTriggerProcessing() {
    setProcessing(true);
    try {
      const result = await depositsApi.triggerProcessing();
      if (result.success && result.stats) {
        setResultDialog({
          open: true,
          variant: "success",
          title: "Processamento concluído",
          description: "Os depósitos foram processados com sucesso.",
          details: [
            { label: "Novos depósitos", value: result.stats.newDepositsCreated },
            { label: "Processados", value: result.stats.depositsProcessed },
            { label: "Minted", value: result.stats.depositsMinted },
            { label: "Falhas", value: result.stats.depositsFailed },
            { label: "Sem match", value: result.stats.depositsUnmatched },
          ],
        });
      } else {
        setResultDialog({
          open: true,
          variant: "error",
          title: "Processamento falhou",
          description: result.message,
        });
      }
      await loadData();
    } catch (error) {
      console.error("Erro ao processar:", error);
      setResultDialog({
        open: true,
        variant: "error",
        title: "Erro ao processar",
        description: "Não foi possível processar os depósitos. Tente novamente.",
      });
    } finally {
      setProcessing(false);
    }
  }

  async function handleReprocess(depositId: string) {
    setConfirmDialog({
      open: true,
      title: "Reprocessar Deposito",
      description: "Deseja reprocessar este deposito? O sistema tentara fazer o mint novamente.",
      loading: false,
      onConfirm: async () => {
        setConfirmDialog((prev) => ({ ...prev, loading: true }));
        try {
          const result = await depositsApi.reprocess(depositId);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "success",
            title: "Depósito reprocessado",
            description: result.message,
          });
          await loadData();
        } catch (error) {
          console.error("Erro ao reprocessar:", error);
          setConfirmDialog((prev) => ({ ...prev, open: false, loading: false }));
          setResultDialog({
            open: true,
            variant: "error",
            title: "Erro ao reprocessar",
            description: "Não foi possível reprocessar o depósito. Tente novamente.",
          });
        }
      },
    });
  }

  function formatDocument(doc: string) {
    if (doc.length === 11) {
      return doc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }
    if (doc.length === 14) {
      return doc.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
    }
    return doc;
  }

  function getStatusBadge(status: Deposit["status"]) {
    const config = STATUS_CONFIG[status];
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${status === "PROCESSING" ? "animate-spin" : ""}`} />
        {config.label}
      </Badge>
    );
  }

  if (loading && deposits.length === 0) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<ArrowDownRight className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Depositos"
          description="Processamento automatico de PIX para BRLD"
        />
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<ArrowDownRight className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Depositos"
        description="Processamento automatico de PIX para BRLD"
        stats={
          stats
            ? [
                { value: stats.total, label: "Total" },
                { value: stats.minted, label: "Minted" },
                { value: stats.pending + stats.failed, label: "Pendentes" },
                { value: `R$ ${stats.totalMintedBRL.toLocaleString("pt-BR")}`, label: "Volume" },
              ]
            : []
        }
        actions={
          <div className="flex gap-2">
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={loadData}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Atualizar
            </Button>
            <Button
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              onClick={handleTriggerProcessing}
              disabled={processing}
            >
              {processing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Play className="w-4 h-4 mr-2" />
              )}
              Processar Agora
            </Button>
          </div>
        }
      />

      {/* Minter Status Alert */}
      {minterStatus && !minterStatus.isReady && (
        <Card className="border-warning bg-warning/10">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-warning">Sistema de Mint Nao Configurado</p>
                <div className="mt-1 text-sm text-muted-foreground space-y-1">
                  {!minterStatus.hasMinterRole && (
                    <p>
                      <span className="text-destructive">&#10005;</span> Wallet nao possui MINTER_ROLE
                      {minterStatus.roleError && `: ${minterStatus.roleError}`}
                    </p>
                  )}
                  {parseFloat(minterStatus.balance) === 0 && (
                    <p>
                      <span className="text-destructive">&#10005;</span> Wallet sem saldo para gas
                    </p>
                  )}
                  {minterStatus.minterAddress && (
                    <p className="font-mono text-xs">Wallet: {minterStatus.minterAddress}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setStatusFilter("")}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold">{stats?.total || 0}</p>
            <p className="text-sm text-muted-foreground">Todos</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "PENDING" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setStatusFilter("PENDING")}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-yellow-500">{stats?.pending || 0}</p>
            <p className="text-sm text-muted-foreground">Pendentes</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "MINTED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setStatusFilter("MINTED")}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-green-500">{stats?.minted || 0}</p>
            <p className="text-sm text-muted-foreground">Minted</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "FAILED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setStatusFilter("FAILED")}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-red-500">{stats?.failed || 0}</p>
            <p className="text-sm text-muted-foreground">Falhas</p>
          </CardContent>
        </Card>
        <Card
          className={`cursor-pointer transition-all ${statusFilter === "UNMATCHED" ? "ring-2 ring-primary" : ""}`}
          onClick={() => setStatusFilter("UNMATCHED")}
        >
          <CardContent className="py-4">
            <p className="text-2xl font-bold text-orange-500">{stats?.unmatched || 0}</p>
            <p className="text-sm text-muted-foreground">Sem Match</p>
          </CardContent>
        </Card>
      </div>

      {/* Minter Info */}
      {minterStatus?.isReady && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wallet className="w-4 h-4" />
              Wallet Minter
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Endereco:</span>{" "}
                <span className="font-mono">{minterStatus.minterAddress}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Saldo Gas:</span>{" "}
                <span className="font-medium">{minterStatus.balance} XDC</span>
              </div>
              <div className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-green-500">MINTER_ROLE ativo</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Deposits Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Depositos {statusFilter && `(${STATUS_CONFIG[statusFilter as keyof typeof STATUS_CONFIG]?.label})`}</span>
            <span className="text-sm font-normal text-muted-foreground">
              {deposits.length} de {total}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {deposits.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Coins className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum deposito encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-border bg-muted/30">
                  <tr className="text-left text-sm text-muted-foreground">
                    <th className="p-4 font-medium">Data</th>
                    <th className="p-4 font-medium">Pagador</th>
                    <th className="p-4 font-medium">Valor</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium">Detalhes</th>
                    <th className="p-4 font-medium">Acoes</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((deposit) => (
                    <tr key={deposit.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                      <td className="p-4">
                        <p className="font-medium">{formatDate(deposit.pixDate)}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(deposit.pixDate).toLocaleTimeString("pt-BR", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </p>
                      </td>
                      <td className="p-4">
                        <div className="flex items-start gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-medium">{deposit.payerName || "Nome nao informado"}</p>
                            <p className="text-xs text-muted-foreground font-mono">
                              {formatDocument(deposit.payerDocument)}
                            </p>
                            {deposit.payerBank && (
                              <p className="text-xs text-muted-foreground">Banco {deposit.payerBank}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="font-semibold text-green-500">
                          {formatCurrency(deposit.amountCents / 100)}
                        </p>
                        {deposit.pixDescription && (
                          <p className="text-xs text-muted-foreground truncate max-w-[150px]">
                            {deposit.pixDescription}
                          </p>
                        )}
                      </td>
                      <td className="p-4">{getStatusBadge(deposit.status)}</td>
                      <td className="p-4 text-sm">
                        {deposit.status === "MINTED" && deposit.mintTxHash && (
                          <div>
                            <a
                              href={`https://explorer.xinfin.network/tx/${deposit.mintTxHash}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              <span className="font-mono text-xs">
                                {deposit.mintTxHash.slice(0, 10)}...
                              </span>
                              <ExternalLink className="w-3 h-3" />
                            </a>
                            <p className="text-xs text-muted-foreground mt-1">
                              {deposit.walletAddress && (
                                <span className="font-mono">{deposit.walletAddress.slice(0, 10)}...</span>
                              )}
                            </p>
                          </div>
                        )}
                        {deposit.status === "FAILED" && deposit.errorMessage && (
                          <p className="text-xs text-destructive max-w-[200px]">
                            {deposit.errorMessage}
                          </p>
                        )}
                        {deposit.status === "UNMATCHED" && (
                          <p className="text-xs text-orange-500">CNPJ nao cadastrado</p>
                        )}
                        {deposit.status === "PENDING" && (
                          <p className="text-xs text-muted-foreground">Aguardando proximo ciclo</p>
                        )}
                        {deposit.status === "PROCESSING" && (
                          <p className="text-xs text-yellow-500">Processando mint...</p>
                        )}
                        {deposit.retryCount > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Tentativas: {deposit.retryCount}/3
                          </p>
                        )}
                      </td>
                      <td className="p-4">
                        {(deposit.status === "FAILED" || deposit.status === "UNMATCHED") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReprocess(deposit.id)}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Reprocessar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog((prev) => ({ ...prev, open: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        description={confirmDialog.description}
        confirmText="Reprocessar"
        variant="default"
        loading={confirmDialog.loading}
      />

      {/* Result Dialog */}
      <ResultDialog
        open={resultDialog.open}
        onClose={() => setResultDialog((prev) => ({ ...prev, open: false }))}
        variant={resultDialog.variant}
        title={resultDialog.title}
        description={resultDialog.description}
        details={resultDialog.details}
      />
    </div>
  );
}
